import { useCallback, useState } from 'react'
import { ApiError } from '../api/client'
import type { MutationResult } from '../api/types'

export interface MutationState {
  run: (call: () => Promise<MutationResult<unknown>>) => Promise<boolean>
  warnings: string[]
  error: string | undefined
  busy: boolean
  /** What the last mutation was, for the heading above its warnings. */
  what: string | undefined
  clear: () => void
}

/** Run one admin mutation and hold on to what it said.
 *
 *  Warnings are kept until they are dismissed, deliberately. Every one of
 *  them describes something the edit did to data that was not on screen —
 *  past completeness recomputed, measurements left under an old parameter —
 *  and a toast that fades after four seconds is how that gets missed. The
 *  backend goes to the trouble of computing them; throwing them away here
 *  would make the whole design pointless.
 */
export function useMutation(): MutationState {
  const [warnings, setWarnings] = useState<string[]>([])
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)
  const [what, setWhat] = useState<string>()

  const clear = useCallback(() => {
    setWarnings([])
    setError(undefined)
    setWhat(undefined)
  }, [])

  const run = useCallback(async (call: () => Promise<MutationResult<unknown>>) => {
    setBusy(true)
    setError(undefined)
    setWarnings([])
    try {
      const result = await call()
      setWarnings(result.warnings ?? [])
      setWhat(undefined)
      return true
    } catch (cause) {
      // 409 carries the collision and how to resolve it, 400 the validation
      // message. Both are written to be read by a person, so both are shown
      // as they are rather than replaced with "something went wrong".
      setError(cause instanceof ApiError
        ? `${cause.status === 409 ? 'Refused' : 'Rejected'}: ${cause.message}`
        : String(cause))
      return false
    } finally {
      setBusy(false)
    }
  }, [])

  return { run, warnings, error, busy, what, clear }
}
